import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Card from 'components/card/Card';
import CustomButton from 'components/CustomButton';
import FileSaver from 'file-saver';
import { queryClient } from 'index';
import { useEffect, useRef, useState } from 'react';
import { FaFilePdf, FaRedo } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { downloadCarnetPdf, findCarnetItems, getCarnet, getCharge, retryCarnet } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import {
  apiErrorMessage,
  blobErrorMessage,
  canRetryCarnet,
  CARNETS_QUERY_KEY,
  carnetInstallmentsLabel,
  carnetItemsRefetchInterval,
  carnetItemStatusMeta,
  carnetPayer,
  carnetRefetchInterval,
  carnetStatusMeta,
  formatIsoDate,
  isCarnetSettled,
  issuedChargeId,
  showsPartialPdfHint,
} from './carnet';

export default function CarnetDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [openingBankPdf, setOpeningBankPdf] = useState<string | null>(null);

  const {
    data: carnet,
    isLoading,
    isError,
    refetch,
  } = useQuery(['getCarnet', id], () => getCarnet(id), {
    enabled: !!id,
    // Para de bater quando o lote chega a um estado final — sem isso o painel
    // consultaria o servidor para sempre. Uma leitura que falha no meio da
    // emissão NÃO para: ver carnetRefetchInterval.
    refetchInterval: (data, query) => carnetRefetchInterval(data, query.state.status === 'error'),
  });

  const {
    data: items,
    isError: itemsError,
    refetch: refetchItems,
  } = useQuery(['findCarnetItems', id], () => findCarnetItems(id), {
    enabled: !!id,
    // Decide pelos próprios dados, não pelo status do lote: o lote pode encerrar
    // entre duas leituras das parcelas, e parar junto deixaria a última presa em
    // "Emitindo". Continua até a leitura mostrar todas em estado final.
    refetchInterval: (data, query) => carnetItemsRefetchInterval(data?.registers, query.state.status === 'error'),
  });

  // A lista só atualiza quando invalidada: sem isto ela voltaria mostrando
  // "Emitindo" para um carnê pronto. Só dispara quando esta tela viu o lote
  // andar — abrir um já pronto não precisa recarregar a lista.
  const status = carnet?.status;
  const sawInFlight = useRef(false);
  useEffect(() => {
    if (!status) return;
    if (!isCarnetSettled(status)) {
      sawInFlight.current = true;
    } else if (sawInFlight.current) {
      sawInFlight.current = false;
      // As parcelas são relidas uma última vez com o lote já encerrado: se a
      // leitura delas tinha parado por qualquer motivo, é aqui que a tabela
      // alcança o cabeçalho.
      queryClient.invalidateQueries({ queryKey: ['findCarnetItems', id] });
      queryClient.invalidateQueries({ queryKey: [CARNETS_QUERY_KEY] });
    }
  }, [status, id]);

  const { mutate: retry, isLoading: retrying } = useMutation(() => retryCarnet(id), {
    onSuccess: ({ requeued }) => {
      // O lote volta a PROCESSING e as parcelas a PENDING: reler as duas queries
      // religa o polling, que tinha parado no estado final.
      queryClient.invalidateQueries({ queryKey: ['getCarnet', id] });
      queryClient.invalidateQueries({ queryKey: ['findCarnetItems', id] });
      queryClient.invalidateQueries({ queryKey: [CARNETS_QUERY_KEY] });
      toast({
        title: requeued === 1 ? '1 parcela reenfileirada.' : `${requeued} parcelas reenfileiradas.`,
        status: 'success',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });
    },
    onError: (err) => {
      toast({
        title: 'Erro ao tentar novamente',
        description: apiErrorMessage(err, 'Não foi possível tentar novamente.'),
        status: 'error',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });
    },
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await downloadCarnetPdf(id);
      const fileName = `carne-${id}.pdf`;
      FileSaver.saveAs(data, fileName);
    } catch (err) {
      // O servidor responde 422 nomeando a parcela que não dá para desenhar com
      // segurança: é essa mensagem que manda o lojista ao PDF do banco.
      toast({
        title: 'Erro ao baixar o carnê',
        description: await blobErrorMessage(err, 'Erro ao baixar o carnê.'),
        status: 'error',
        duration: 9000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  // Saída de emergência: o PDF oficial do banco da parcela, para quando a ficha
  // desenhada não puder ser gerada ou o lojista preferir a do banco.
  const handleBankPdf = async (chargeId: string) => {
    // A aba abre ANTES do await: Safari e iOS bloqueiam em silêncio um
    // window.open fora do gesto do usuário.
    const tab = window.open('', '_blank');
    setOpeningBankPdf(chargeId);
    try {
      const charge = await getCharge(chargeId);
      const url = charge.Billet?.[0]?.billetUrl;
      if (!url) {
        tab?.close();
        toast({
          title: 'PDF indisponível',
          description: 'O PDF oficial do banco ainda não está disponível para esta parcela.',
          status: 'warning',
          duration: 6000,
          position: 'top',
          isClosable: true,
        });
        return;
      }
      if (tab && !tab.closed) tab.location.href = url;
      else window.location.assign(url);
    } catch (err) {
      tab?.close();
      toast({
        title: 'Erro',
        description: apiErrorMessage(err, 'Não foi possível abrir o PDF do banco.'),
        status: 'error',
        duration: 6000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setOpeningBankPdf(null);
    }
  };

  const reload = () => {
    refetch();
    refetchItems();
  };

  if (isLoading) {
    return (
      <Flex justify="center" py={16}>
        <Spinner />
      </Flex>
    );
  }

  // Só quando nunca chegou dado. Com o carnê já na tela, uma releitura que
  // falha não o esconde: a última leitura boa continua valendo e o polling
  // segue tentando.
  if (!carnet) {
    return (
      <Card p="24px">
        <Text color="red.500" mb={3}>
          Não foi possível carregar o carnê.
        </Text>
        <HStack spacing={3}>
          <CustomButton variant="solid" onClick={reload}>
            Tentar novamente
          </CustomButton>
          <CustomButton variant="outline" onClick={() => navigate('/access/carnes')}>
            Voltar
          </CustomButton>
        </HStack>
      </Card>
    );
  }

  const statusMeta = carnetStatusMeta(carnet.status);
  const payer = carnetPayer(carnet);

  return (
    <Box>
      <Card p="20px" mb={5}>
        <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={4}>
          <Box>
            <HStack spacing={3} mb={1} flexWrap="wrap">
              <Text fontSize="2xl" fontWeight="bold" color="navy.700">
                {carnet.title || 'Carnê'}
              </Text>
              <Badge colorScheme={statusMeta.colorScheme}>{statusMeta.label}</Badge>
            </HStack>
            {payer && (
              <Text color="gray.700" fontWeight="medium">
                {payer.name}
                {payer.document && ` · ${payer.document}`}
              </Text>
            )}
            <Text color="gray.600">
              {carnetInstallmentsLabel(carnet, maskCurrency)} · 1º vencimento{' '}
              {formatIsoDate(carnet.metadata?.firstDueDate)}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {carnet.succeededItems} de {carnet.totalItems} emitidos
            </Text>
            {carnet.failedItems > 0 && (
              <Text color="red.500" fontSize="sm">
                {carnet.failedItems} {carnet.failedItems === 1 ? 'parcela falhou' : 'parcelas falharam'}.
              </Text>
            )}
          </Box>

          <Flex gap={3} direction={{ base: 'column', md: 'row' }}>
            {canRetryCarnet(carnet) && (
              <CustomButton
                variant="outline"
                colorScheme="orange"
                leftIcon={<FaRedo />}
                isLoading={retrying}
                onClick={() => retry()}
              >
                Tentar novamente as que falharam
              </CustomButton>
            )}
            {/* A partir da primeira parcela emitida: esperar as 24 travaria o
                lojista por causa de uma que falhou. */}
            {carnet.succeededItems > 0 && (
              <CustomButton variant="solid" leftIcon={<FaFilePdf />} isLoading={downloading} onClick={handleDownload}>
                Baixar carnê em PDF
              </CustomButton>
            )}
          </Flex>
        </Flex>

        {showsPartialPdfHint(carnet) && (
          <Text color="gray.500" fontSize="sm" mt={3}>
            O PDF traz as parcelas já emitidas. Baixe de novo depois que as demais forem emitidas.
          </Text>
        )}

        {/* A última leitura boa segue na tela; o aviso evita que ela passe por atual. */}
        {(isError || (itemsError && !!items)) && (
          <Text color="orange.500" fontSize="sm" mt={3}>
            Não foi possível atualizar os dados agora. O que aparece aqui é a última leitura.{' '}
            <Button size="sm" variant="link" colorScheme="orange" onClick={reload}>
              Atualizar
            </Button>
          </Text>
        )}
      </Card>

      <Card p="0px" overflow="hidden">
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Parcela</Th>
                <Th>Vencimento</Th>
                <Th isNumeric>Valor</Th>
                <Th>Status</Th>
                <Th>Detalhe</Th>
                <Th textAlign="right">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {!items && (
                <Tr>
                  <Td colSpan={6} py={8} textAlign="center">
                    {itemsError ? (
                      <Button size="sm" variant="link" colorScheme="red" onClick={() => refetchItems()}>
                        Não foi possível carregar as parcelas. Tentar novamente
                      </Button>
                    ) : (
                      <Spinner size="sm" />
                    )}
                  </Td>
                </Tr>
              )}
              {items?.registers.map((item) => {
                const itemMeta = carnetItemStatusMeta(item.status);
                const chargeId = issuedChargeId(item);
                return (
                  <Tr key={item.id} _hover={{ bg: 'gray.50' }}>
                    <Td fontWeight="500" color="navy.700">
                      {item.index + 1}/{carnet.totalItems}
                    </Td>
                    <Td>{formatIsoDate(item.payload?.dueDate)}</Td>
                    <Td isNumeric fontWeight="600" color="navy.700">
                      {maskCurrency(item.payload?.amount ?? 0)}
                    </Td>
                    <Td>
                      <Badge colorScheme={itemMeta.colorScheme}>{itemMeta.label}</Badge>
                    </Td>
                    <Td>
                      {item.status === 'FAILED' && item.error?.message ? (
                        <Text fontSize="xs" color="red.500">
                          {item.error.message}
                        </Text>
                      ) : (
                        <Text as="span" color="gray.300">
                          —
                        </Text>
                      )}
                    </Td>
                    <Td textAlign="right">
                      {chargeId && (
                        <HStack spacing={3} justify="flex-end">
                          <Button
                            size="xs"
                            variant="link"
                            colorScheme="brand"
                            onClick={() => navigate(`/access/charge/${chargeId}`)}
                          >
                            Ver cobrança
                          </Button>
                          <Button
                            size="xs"
                            variant="link"
                            colorScheme="brand"
                            isLoading={openingBankPdf === chargeId}
                            onClick={() => handleBankPdf(chargeId)}
                          >
                            PDF do banco
                          </Button>
                        </HStack>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
}
